import { useState } from "react";

import { CircleInformation, RefreshIcon } from "@/assets/icons";
import PageErrorImage from "@/assets/svg/pageError.svg?react";
import { UnlockPremiumModal } from "@/components/dataset/datasetExplorer/UnlockPremiumModal";
import { Button } from "@/components/ui/button";
import { LLM_ERROR_REASON } from "@/constants/errorCode";
import { MESSAGE } from "@/constants/message";
import type { ExecutiveSummaryError } from "@/hooks/analysisSummary/useExecutiveSummary";

const { AI_SUMMARY_ERROR } = MESSAGE;

export interface AISummaryErrorProps {
  error?: ExecutiveSummaryError | null;
  onRetry?: () => void;
}

/**
 * Quota is an account/billing limit support can't unblock — the workspace admin
 * owns it — so that state omits the support link. Every other failure gets it.
 */
const REASONS_WITHOUT_SUPPORT_LINK: ReadonlySet<string> = new Set([
  LLM_ERROR_REASON.QUOTA_EXCEEDED
]);

const resolveErrorCopy = (error?: ExecutiveSummaryError | null) => {
  const reason = error?.reason;

  return {
    description:
      (reason ? AI_SUMMARY_ERROR.DESCRIPTION_BY_REASON[reason] : undefined) ??
      AI_SUMMARY_ERROR.DEFAULT_DESCRIPTION,
    showSupportLink: !reason || !REASONS_WITHOUT_SUPPORT_LINK.has(reason)
  };
};

export const AISummaryError = ({
  error,
  onRetry
}: Readonly<AISummaryErrorProps>) => {
  const { description, showSupportLink } = resolveErrorCopy(error);
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center h-full gap-2 px-6 py-10 min-h-[65vh] text-center"
    >
      <PageErrorImage className="w-52 h-auto" aria-hidden="true" />
      <p className="text-lg font-bold text-txtHeading">
        {AI_SUMMARY_ERROR.TITLE}
      </p>
      <p className="max-w-sm text-sm text-txtBody">{description}</p>
      {onRetry && (
        <Button
          className="px-6 mt-3 rounded-lg [&_path]:fill-white"
          onClick={onRetry}
        >
          <RefreshIcon />
          {AI_SUMMARY_ERROR.RETRY}
        </Button>
      )}
      {showSupportLink && (
        <p className="flex items-center gap-1 mt-2 text-sm text-txtBody">
          <CircleInformation className="w-4 h-4 fill-txtBody" />
          {AI_SUMMARY_ERROR.NEED_HELP}
          <button
            type="button"
            className="text-sm font-semibold text-primary-teal hover:underline"
            onClick={() => setIsSupportOpen(true)}
          >
            {AI_SUMMARY_ERROR.CONTACT_SUPPORT}
          </button>
        </p>
      )}
      {isSupportOpen && (
        <UnlockPremiumModal
          title={AI_SUMMARY_ERROR.SUPPORT_MODAL_TITLE}
          contactUsDesc={AI_SUMMARY_ERROR.SUPPORT_MODAL_DESCRIPTION}
          emailSubject={AI_SUMMARY_ERROR.SUPPORT_EMAIL_SUBJECT}
          onClose={() => setIsSupportOpen(false)}
        />
      )}
    </div>
  );
};
